import type { Edge, Node } from 'reactflow';
import { MarkerType, Position } from 'reactflow';

export type BillFlowActorKind = 'person' | 'agent' | 'system';
export type BillFlowActor = { kind: BillFlowActorKind; label: string };
export type BillFlowTone = 'start' | 'main' | 'decision' | 'end' | 'reject' | 'pending' | 'plain';

export type BillFlowNodeData = {
  title: string[];
  sub?: string[];
  actors: BillFlowActor[];
  location?: string;
  tone?: BillFlowTone;
};

export const BILL_FLOW_NODE_TYPE = 'billFlowCard';
export const BILL_FLOW_EDGE_TYPE = 'smoothstep';

/** Spacious grid — same board feel as org chart / inspiration boards. */
export const BILL_FLOW_LAYOUT = {
  cardW: 230,
  /** PO column footprint (fits 3 requester / payment / decide-fork cards). */
  poColW: 760,
  colGap: 100,
  rowGap: 72,
  padX: 80,
  padY: 64,
  hDecision: 118,
  hStart: 132,
  hBody: 132,
  hBodyTall: 168,
  hEnd: 124,
};

const L = BILL_FLOW_LAYOUT;

/** Column 0 = PO (wide); 1..4 = Timber, CNC, Designer, Engineering. */
function colX(col: number): number {
  if (col === 0) return L.padX;
  return L.padX + L.poColW + L.colGap + (col - 1) * (L.cardW + L.colGap);
}

function node(
  id: string,
  x: number,
  y: number,
  w: number,
  h: number,
  data: BillFlowNodeData,
  opts?: { sourcePosition?: Position; targetPosition?: Position },
): Node<BillFlowNodeData> {
  return {
    id,
    type: BILL_FLOW_NODE_TYPE,
    position: { x, y },
    data,
    sourcePosition: opts?.sourcePosition ?? Position.Bottom,
    targetPosition: opts?.targetPosition ?? Position.Top,
    draggable: false,
    selectable: false,
    style: { width: w, height: h },
  };
}

function edge(
  id: string,
  source: string,
  target: string,
  label?: string,
): Edge {
  return {
    id,
    source,
    target,
    type: BILL_FLOW_EDGE_TYPE,
    label,
    labelStyle: { fill: '#6b655c', fontWeight: 700, fontSize: 11 },
    labelBgStyle: { fill: '#f7f5f1', fillOpacity: 0.92 },
    labelBgPadding: [6, 4] as [number, number],
    labelBgBorderRadius: 4,
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#8a8478' },
    style: { stroke: '#8a8478', strokeWidth: 1.6 },
  };
}

/**
 * Builds the Bills & Payables decision tree as React Flow nodes/edges
 * with generous column/row spacing (pan/zoom board, not a fixed SVG squeeze).
 */
export function buildBillPayFlowGraph(): { nodes: Node<BillFlowNodeData>[]; edges: Edge[]; width: number; height: number } {
  const nodes: Node<BillFlowNodeData>[] = [];
  const edges: Edge[] = [];

  const poColW = L.poColW;
  const poCardW = 320;
  const w = L.cardW;
  const poX = colX(0);
  const poCardX = poX + (poColW - poCardW) / 2;
  const timberX = colX(1);
  const cncX = colX(2);
  const designX = colX(3);
  const engX = colX(4);

  /* Root centered over the five columns */
  const totalW = engX + w - poX;
  const rootW = 320;
  const rootX = poX + (totalW - rootW) / 2;
  let y = L.padY;

  nodes.push(
    node('root', rootX, y, rootW, L.hDecision, {
      tone: 'decision',
      actors: [{ kind: 'person', label: 'Staff' }],
      location: 'CRM',
      title: ['What is being bought?'],
    }),
  );
  y += L.hDecision + L.rowGap;

  /* —— PO column —— */
  let poY = y;
  nodes.push(
    node('po-who', poCardX, poY, poCardW, L.hDecision, {
      tone: 'decision',
      actors: [{ kind: 'person', label: 'Staff' }],
      location: 'Purchasing',
      title: ['Who requests?'],
    }),
  );
  edges.push(edge('e-root-po', 'root', 'po-who', 'PO purchase'));
  poY += L.hDecision + L.rowGap;

  const reqW = 210;
  const reqGap = 28;
  const reqTotal = reqW * 3 + reqGap * 2;
  const req1X = poX + (poColW - reqTotal) / 2;
  const req2X = req1X + reqW + reqGap;
  const req3X = req2X + reqW + reqGap;
  const reqY = poY;

  nodes.push(
    node('po-emp', req1X, reqY, reqW, L.hStart, {
      tone: 'start',
      actors: [{ kind: 'person', label: 'Employee' }],
      location: 'Purchasing',
      title: ['Employee'],
      sub: ['PR or Slack ask'],
    }),
    node('po-pm', req2X, reqY, reqW, L.hStart, {
      tone: 'start',
      actors: [{ kind: 'person', label: 'PM' }],
      location: 'Purchasing',
      title: ['Project manager'],
      sub: ['CRM Purchasing'],
    }),
    node('po-grok', req3X, reqY, reqW, L.hStart, {
      tone: 'start',
      actors: [
        { kind: 'agent', label: 'Hugh' },
        { kind: 'agent', label: 'Caesar' },
      ],
      location: 'Slack / agents',
      title: ['Agent requesters'],
      sub: ['Hugh · Caesar'],
    }),
  );
  edges.push(
    edge('e-who-emp', 'po-who', 'po-emp'),
    edge('e-who-pm', 'po-who', 'po-pm'),
    edge('e-who-grok', 'po-who', 'po-grok'),
  );
  poY = reqY + L.hStart + L.rowGap;

  nodes.push(
    node('po-quote', poCardX, poY, poCardW, L.hBody, {
      actors: [
        { kind: 'person', label: 'Requester' },
        { kind: 'agent', label: 'Agent' },
      ],
      location: 'Other Quotes',
      title: ['Request + quote'],
      sub: ['Upload under Other Quotes'],
    }),
  );
  edges.push(
    edge('e-emp-quote', 'po-emp', 'po-quote'),
    edge('e-pm-quote', 'po-pm', 'po-quote'),
    edge('e-grok-quote', 'po-grok', 'po-quote'),
  );
  poY += L.hBody + L.rowGap;

  nodes.push(
    node('po-bands', poCardX, poY, poCardW, L.hBodyTall, {
      tone: 'decision',
      actors: [{ kind: 'system', label: 'DoA bands' }],
      location: 'Purchasing',
      title: ['Approval by $ (job)'],
      sub: [
        '$0–500: Project Manager',
        '$500–2.5k: Supervisor → Executive',
        '$2.5k+: Sup → PM → Ctrl → Exec',
      ],
    }),
  );
  edges.push(edge('e-quote-bands', 'po-quote', 'po-bands'));
  poY += L.hBodyTall + L.rowGap;

  nodes.push(
    node('po-decide', poCardX, poY, poCardW, L.hDecision, {
      tone: 'decision',
      actors: [{ kind: 'person', label: 'Approver' }],
      location: 'Purchasing',
      title: ['Approve, return, or reject?'],
    }),
  );
  edges.push(edge('e-bands-decide', 'po-bands', 'po-decide'));
  poY += L.hDecision + L.rowGap;

  /* Three outcomes: reject | send back (status returned) | approve.
   * Status name from PurchaseRequisitionStatus + returnPurchaseRequisition. */
  const forkW = 220;
  const forkGap = 28;
  const forkTotal = forkW * 3 + forkGap * 2;
  const rejX = poX + (poColW - forkTotal) / 2;
  const retX = rejX + forkW + forkGap;
  const issX = retX + forkW + forkGap;
  nodes.push(
    node('po-reject', rejX, poY, forkW, L.hEnd, {
      tone: 'reject',
      actors: [{ kind: 'system', label: 'System' }],
      location: 'Purchasing',
      title: ['END'],
      sub: ['Rejected'],
    }),
    node('po-returned', retX, poY, forkW, L.hBody, {
      tone: 'pending',
      actors: [{ kind: 'system', label: 'System' }],
      location: 'Purchasing',
      title: ['Returned'],
      sub: ['status: returned', 'Requester edits'],
    }),
    node('po-issued', issX, poY, forkW, L.hEnd, {
      actors: [{ kind: 'system', label: 'CRM' }],
      location: 'Purchasing',
      title: ['PO issued'],
      sub: ['Notification sent'],
    }),
  );
  edges.push(
    edge('e-decide-rej', 'po-decide', 'po-reject', 'Reject'),
    edge('e-decide-ret', 'po-decide', 'po-returned', 'Send back'),
    edge('e-decide-iss', 'po-decide', 'po-issued', 'Approve'),
    /* Loop: returned PR → requester edits Request + quote and resubmits */
    {
      ...edge('e-ret-quote', 'po-returned', 'po-quote', 'Resubmit'),
      sourceHandle: 'loop',
      targetHandle: 'in',
      style: { stroke: '#a0894a', strokeWidth: 1.6, strokeDasharray: '6 4' },
      markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: '#a0894a' },
      labelStyle: { fill: '#8a7340', fontWeight: 700, fontSize: 11 },
      labelBgStyle: { fill: '#f7f5f1', fillOpacity: 0.92 },
    },
  );
  poY += L.hBody + L.rowGap;

  nodes.push(
    node('po-pay', poCardX, poY, poCardW, L.hDecision, {
      tone: 'decision',
      actors: [{ kind: 'person', label: 'PM' }],
      location: 'Purchasing',
      title: ['Payment method?'],
    }),
  );
  edges.push(edge('e-iss-pay', 'po-issued', 'po-pay'));
  poY += L.hDecision + L.rowGap;

  const payW = 210;
  const payGap = 28;
  const payTotal = payW * 3 + payGap * 2;
  const pay1X = poX + (poColW - payTotal) / 2;
  const pay2X = pay1X + payW + payGap;
  const pay3X = pay2X + payW + payGap;
  const payY = poY;

  nodes.push(
    node('pay-card', pay1X, payY, payW, L.hBodyTall, {
      actors: [
        { kind: 'system', label: 'System' },
        { kind: 'agent', label: 'Hugh' },
      ],
      location: 'Purchasing / #receipts',
      title: ['Match card charges'],
      sub: ['Sequence/QB auto-match', 'Hugh links from #receipts'],
    }),
    node('pay-bill', pay2X, payY, payW, L.hBody, {
      tone: 'main',
      actors: [
        { kind: 'person', label: 'PM' },
        { kind: 'person', label: 'Exec' },
      ],
      location: 'Billing & Payables',
      title: ['Mark ready to pay'],
      sub: ['pending → ready / installment'],
    }),
    node('pay-precon', pay3X, payY, payW, L.hBody, {
      actors: [{ kind: 'person', label: 'Juliana' }],
      location: 'QuickBooks',
      title: ['Pay in QB'],
      sub: ['skip B&P'],
    }),
  );
  edges.push(
    edge('e-pay-card', 'po-pay', 'pay-card', 'Card'),
    edge('e-pay-bill', 'po-pay', 'pay-bill', 'Bill · contract'),
    edge('e-pay-precon', 'po-pay', 'pay-precon', 'Bill · precon'),
  );

  let payColY = payY + L.hBodyTall + L.rowGap;
  nodes.push(
    node('pay-card-match', pay1X, payColY, payW, L.hBody, {
      actors: [
        { kind: 'agent', label: 'Hugh' },
        { kind: 'person', label: 'Matthew' },
      ],
      location: 'Purchasing',
      title: ['3-day unmatched check'],
      sub: ['Hugh (purchasing bot)', 'Flag leftovers to Matthew'],
    }),
    node('pay-bill-qb', pay2X, payColY, payW, L.hBodyTall, {
      tone: 'main',
      actors: [
        { kind: 'system', label: 'Cloud Functions' },
        { kind: 'person', label: 'Juliana' },
      ],
      location: 'QB + Billing & Payables',
      title: ['CRM creates QB bill'],
      sub: [
        'Approved amount only (idempotent)',
        'Due date from vendor terms',
        'Juliana pays in QB',
      ],
    }),
    node('pay-precon-2', pay3X, payColY, payW, L.hDecision, {
      actors: [{ kind: 'person', label: 'PM' }],
      location: 'Purchasing',
      title: ['Mark purchased'],
    }),
  );
  edges.push(
    edge('e-card-match', 'pay-card', 'pay-card-match'),
    edge('e-bill-qb', 'pay-bill', 'pay-bill-qb'),
    edge('e-precon-2', 'pay-precon', 'pay-precon-2'),
  );
  payColY += L.hBodyTall + L.rowGap;
  nodes.push(
    node('pay-card-2', pay1X, payColY, payW, L.hDecision, {
      actors: [
        { kind: 'person', label: 'PM' },
        { kind: 'person', label: 'Office / COO' },
        { kind: 'agent', label: 'Hugh' },
      ],
      location: 'Purchasing',
      title: ['Mark purchased'],
      sub: ['PM, office/COO, or Hugh'],
    }),
    node('pay-bill-end', pay2X, payColY, payW, L.hEnd, {
      tone: 'end',
      actors: [{ kind: 'system', label: 'System' }],
      location: 'Purchasing',
      title: ['END'],
      sub: ['ACH PO → purchased', 'when linked QB bill paid'],
    }),
    node('pay-precon-end', pay3X, payColY, payW, L.hEnd, {
      tone: 'end',
      actors: [{ kind: 'system', label: 'System' }],
      location: 'Purchasing',
      title: ['END'],
      sub: ['PO purchased'],
    }),
  );
  edges.push(
    edge('e-card-2', 'pay-card-match', 'pay-card-2'),
    edge('e-bill-end', 'pay-bill-qb', 'pay-bill-end'),
    edge('e-precon-end', 'pay-precon-2', 'pay-precon-end'),
  );
  payColY += L.hDecision + L.rowGap;
  nodes.push(
    node('pay-card-end', pay1X, payColY, payW, L.hEnd, {
      tone: 'end',
      actors: [{ kind: 'system', label: 'System' }],
      location: 'Purchasing',
      title: ['END'],
      sub: ['PO purchased'],
    }),
  );
  edges.push(edge('e-card-end', 'pay-card-2', 'pay-card-end'));

  const poBottom = payColY + L.hEnd;

  /* —— Package branch helper (timber / CNC) —— */
  function packageBranch(
    prefix: string,
    x: number,
    startTitle: string,
    startSub: string,
    startLoc: string,
    decideLoc: string,
    rootLabel: string,
  ) {
    let by = y;
    nodes.push(
      node(`${prefix}-1`, x, by, w, L.hStart, {
        tone: 'start',
        actors: [{ kind: 'person', label: 'PM' }],
        location: startLoc,
        title: [startTitle],
        sub: [startSub],
      }),
    );
    edges.push(edge(`e-root-${prefix}`, 'root', `${prefix}-1`, rootLabel));
    by += L.hStart + L.rowGap;

    nodes.push(
      node(`${prefix}-2`, x, by, w, L.hBody, {
        actors: [{ kind: 'person', label: 'PM' }],
        location: 'Project Hub · RFQ',
        title: ['Push to RFQ'],
        sub: ['Release & publish'],
      }),
    );
    edges.push(edge(`e-${prefix}-2`, `${prefix}-1`, `${prefix}-2`));
    by += L.hBody + L.rowGap;

    nodes.push(
      node(`${prefix}-3`, x, by, w, L.hDecision, {
        actors: [{ kind: 'person', label: 'Supplier' }],
        location: 'Vendor portal',
        title: ['Upload RFQ quote'],
      }),
    );
    edges.push(edge(`e-${prefix}-3`, `${prefix}-2`, `${prefix}-3`));
    by += L.hDecision + L.rowGap;

    nodes.push(
      node(`${prefix}-decide`, x, by, w, L.hDecision, {
        tone: 'decision',
        actors: [{ kind: 'person', label: 'PM' }],
        location: decideLoc,
        title: ['Award or reject?'],
      }),
    );
    edges.push(edge(`e-${prefix}-decide`, `${prefix}-3`, `${prefix}-decide`));
    by += L.hDecision + L.rowGap;

    const half = (w - 16) / 2;
    nodes.push(
      node(`${prefix}-rej`, x, by, half, L.hEnd, {
        tone: 'reject',
        actors: [{ kind: 'system', label: 'System' }],
        location: decideLoc,
        title: ['END'],
        sub: ['Rejected'],
      }),
      node(`${prefix}-aw`, x + half + 16, by, half, L.hEnd, {
        tone: 'start',
        actors: [{ kind: 'person', label: 'PM' }],
        location: decideLoc,
        title: ['Awarded'],
      }),
    );
    edges.push(
      edge(`e-${prefix}-rej`, `${prefix}-decide`, `${prefix}-rej`, 'Reject'),
      edge(`e-${prefix}-aw`, `${prefix}-decide`, `${prefix}-aw`, 'Award'),
    );
    by += L.hEnd + L.rowGap;

    nodes.push(
      node(`${prefix}-bill`, x, by, w, L.hBody, {
        actors: [{ kind: 'agent', label: 'CRM' }],
        location: 'Billing & Payables',
        title: ['Bill on B&P'],
        sub: ['Pending · no PO'],
      }),
    );
    edges.push(edge(`e-${prefix}-bill`, `${prefix}-aw`, `${prefix}-bill`));
    by += L.hBody + L.rowGap;

    nodes.push(
      node(`${prefix}-ready`, x, by, w, L.hBody, {
        actors: [
          { kind: 'person', label: 'PM' },
          { kind: 'person', label: 'Exec' },
        ],
        location: 'Billing & Payables',
        title: ['Mark ready to pay'],
        sub: ['Terms set · notify Juliana'],
      }),
    );
    edges.push(edge(`e-${prefix}-ready`, `${prefix}-bill`, `${prefix}-ready`));
    by += L.hBody + L.rowGap;

    nodes.push(
      node(`${prefix}-qb`, x, by, w, L.hBodyTall, {
        tone: 'main',
        actors: [
          { kind: 'agent', label: 'CRM' },
          { kind: 'person', label: 'Juliana' },
        ],
        location: 'QB + Billing & Payables',
        title: ['CRM creates QB bill'],
        sub: [
          'Approved amount only (idempotent)',
          'Due date from vendor terms',
          'Juliana pays in QB',
        ],
      }),
    );
    edges.push(edge(`e-${prefix}-qb`, `${prefix}-ready`, `${prefix}-qb`));
    by += L.hBodyTall + L.rowGap;

    nodes.push(
      node(`${prefix}-end`, x, by, w, L.hEnd, {
        tone: 'end',
        actors: [{ kind: 'system', label: 'System' }],
        location: 'Billing & Payables',
        title: ['END'],
        sub: ['Paid · no PO'],
      }),
    );
    edges.push(edge(`e-${prefix}-end`, `${prefix}-qb`, `${prefix}-end`));
    return by + L.hEnd;
  }

  const timberBottom = packageBranch(
    'timber',
    timberX,
    'Timber list',
    'On proposal / deal',
    'Project Hub · Files',
    'Timber RFQ',
    'Timber package',
  );
  const cncBottom = packageBranch(
    'cnc',
    cncX,
    'CNC package',
    'Outside vendor',
    'Project Hub · CNC',
    'CNC RFQ',
    'CNC package',
  );

  /* —— Invoice branches (Designer / Engineering) —— */
  function invoiceBranch(
    prefix: string,
    x: number,
    startActors: BillFlowActor[],
    startTitle: string,
    startSub: string,
    allocSub: string,
    issuedSub: string,
    rootLabel: string,
  ) {
    let iy = y;
    nodes.push(
      node(`${prefix}-1`, x, iy, w, L.hStart, {
        tone: 'start',
        actors: startActors,
        location: 'Invoice review',
        title: [startTitle],
        sub: [startSub],
      }),
    );
    edges.push(edge(`e-root-${prefix}`, 'root', `${prefix}-1`, rootLabel));
    iy += L.hStart + L.rowGap;

    nodes.push(
      node(`${prefix}-2`, x, iy, w, L.hBodyTall, {
        actors: [
          { kind: 'agent', label: 'CRM agent' },
          { kind: 'agent', label: 'Caesar' },
        ],
        location: 'Invoice review',
        title: ['Job-cost / allocate'],
        sub: [allocSub, 'CRM agent + Caesar enqueue'],
      }),
    );
    edges.push(edge(`e-${prefix}-2`, `${prefix}-1`, `${prefix}-2`));
    iy += L.hBodyTall + L.rowGap;

    nodes.push(
      node(`${prefix}-decide`, x, iy, w, L.hBodyTall, {
        tone: 'decision',
        actors: [
          { kind: 'person', label: 'Juliana' },
          { kind: 'person', label: 'Matthew' },
        ],
        location: 'Purchasing bands',
        title: ['Approve or reject?'],
        sub: ['Staff on DoA bands', 'Never bots'],
      }),
    );
    edges.push(edge(`e-${prefix}-decide`, `${prefix}-2`, `${prefix}-decide`));
    iy += L.hBodyTall + L.rowGap;

    const half = (w - 16) / 2;
    nodes.push(
      node(`${prefix}-rej`, x, iy, half, L.hEnd, {
        tone: 'reject',
        actors: [{ kind: 'system', label: 'System' }],
        location: 'Invoice review',
        title: ['END'],
        sub: ['Rejected'],
      }),
      node(`${prefix}-iss`, x + half + 16, iy, half, L.hEnd, {
        actors: [{ kind: 'system', label: 'CRM' }],
        location: 'Purchasing',
        title: ['PO issued'],
        sub: [issuedSub],
      }),
    );
    edges.push(
      edge(`e-${prefix}-rej`, `${prefix}-decide`, `${prefix}-rej`, 'Reject'),
      edge(`e-${prefix}-iss`, `${prefix}-decide`, `${prefix}-iss`, 'Approve'),
    );
    iy += L.hEnd + L.rowGap;

    nodes.push(
      node(`${prefix}-method`, x, iy, w, L.hDecision, {
        tone: 'decision',
        actors: [{ kind: 'person', label: 'Staff' }],
        location: 'Invoice review',
        title: ['Payment method?'],
      }),
    );
    edges.push(edge(`e-${prefix}-method`, `${prefix}-iss`, `${prefix}-method`));
    iy += L.hDecision + L.rowGap;

    const payHalf = (w - 16) / 2;
    nodes.push(
      node(`${prefix}-ach`, x, iy, payHalf, L.hBodyTall, {
        tone: 'main',
        actors: [
          { kind: 'agent', label: 'CRM' },
          { kind: 'person', label: 'Juliana' },
        ],
        location: 'QB + B&P',
        title: ['Bill Payment'],
        sub: ['supplierBills + QB bill', 'Juliana pays in QB'],
      }),
      node(`${prefix}-card`, x + payHalf + 16, iy, payHalf, L.hBodyTall, {
        actors: [{ kind: 'agent', label: 'Caesar' }],
        location: 'Project Card',
        title: ['Card under $2k'],
        sub: ['Handoff → charge', 'markPurchased'],
      }),
    );
    edges.push(
      edge(`e-${prefix}-ach`, `${prefix}-method`, `${prefix}-ach`, 'ACH'),
      edge(`e-${prefix}-card`, `${prefix}-method`, `${prefix}-card`, 'Card'),
    );
    iy += L.hBodyTall + L.rowGap;

    nodes.push(
      node(`${prefix}-end`, x, iy, w, L.hEnd, {
        tone: 'end',
        actors: [{ kind: 'system', label: 'System' }],
        location: 'Purchasing',
        title: ['END'],
        sub: ['ACH: PO purchased on QB pay', 'Card: mark purchased'],
      }),
    );
    edges.push(
      edge(`e-${prefix}-end-ach`, `${prefix}-ach`, `${prefix}-end`),
      edge(`e-${prefix}-end-card`, `${prefix}-card`, `${prefix}-end`),
    );
    return iy + L.hEnd;
  }

  const designBottom = invoiceBranch(
    'design',
    designX,
    [
      { kind: 'person', label: 'Designer' },
      { kind: 'agent', label: 'CRM agent' },
      { kind: 'agent', label: 'Caesar' },
    ],
    'Submit or enqueue',
    'Queue / CRM agent / Caesar',
    'external_design',
    'Final gate',
    'Designer invoice',
  );
  const engBottom = invoiceBranch(
    'eng',
    engX,
    [
      { kind: 'person', label: 'Vendor' },
      { kind: 'agent', label: 'CRM agent' },
      { kind: 'agent', label: 'Caesar' },
    ],
    'FTET / eng invoice',
    'Same review queue',
    'external_engineering',
    'Eng. bucket',
    'Engineering invoice',
  );

  const width = engX + w + L.padX;
  const height = Math.max(poBottom, timberBottom, cncBottom, designBottom, engBottom) + L.padY;

  return { nodes, edges, width, height };
}
